import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useDispatch } from 'react-redux'

import { useTheme } from '../../context/theme'
import {
  DMPrimaryButton,
  DMPrimaryAltButton,
  DMModal
} from '@decormatters/dm-theme'
import ReactCrop from 'react-image-crop'

import { fetchFeed } from '../../redux/feedInfiniteSlice'
import {
  setModalOpen,
  setModalClose
} from '../../redux/designerReducers/designerReducer'
import { createMyDecor, removebg } from '../../redux/apis'
import { FEED_RESET } from '../../redux/constants'
import { randomNodeId } from '../../utils/nodeUtils'

import 'react-image-crop/dist/ReactCrop.css'

import ModalContentContainer from './ModalContentContainer'
import ModalActionContainer from './ModalActionContainer'
import useModal from '../../hooks/useModal'
import styled from 'styled-components'

import { Player } from '@lottiefiles/react-lottie-player'
import circle from '../../assets/throbber.json'
import { logEvent } from '../../utils/firebase'

const CropMyDecorModalContainer = styled(DMModal)`
  background-color: ${props => props.theme.background.secondary};
  background-image: url('${props => props.theme.modal.upload}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`

const CropContainer = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
`

const CropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2;
  color: white;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Loading = styled.div`
  /*background-color: green;*/
`

const CropMyDecorModal = forwardRef(
  (
    {
      show,
      toCrop,
      onClose,
      onComplete,
      onRemoveBGComplete,
      category,
      ...props
    },
    ref
  ) => {
    const dispatch = useDispatch()
    const { appearance } = useTheme()
    useModal(show)

    const rImg = useRef(null)

    const pixelRatio = 4
    //const [image, setImage] = useState()
    const [crop, setCrop] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [removeStatus, setRemoveStatus] = useState('none')
    const [completedCrop, setCompletedCrop] = useState(null)

    //const [viewImgHeight, setViewImgHeight] = useState('600px')
    const [viewImgWidth, setViewImgWidth] = useState('600px')

    const VIEW_MAX_HEIGHT = 600
    const VIEW_MAX_WIDTH = 600

    const cropAreaStyles = {
      width: viewImgWidth,
      backgroundColor: 'white',
      backgroundImage: `linear-gradient(45deg, #d4d4d4 25%, transparent 25%),
    linear-gradient(-45deg, #d4d4d4 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #d4d4d4 75%),
    linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)`,
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
      borderRadius: '10px',
      display: 'block'
    }

    useEffect(() => {
      if (show) {
        setSubmitting(false)
        setRemoveStatus('none')
        setCompletedCrop(null)
      }
    }, [show])

    const fullImageCrop = useCallback(
      ({ w, h }) => {
        if (show) {
          if (!w) w = rImg.current.width
          if (!h) h = rImg.current.height

          const x = 0
          const y = 0

          const c = {
            width: w,
            height: h,
            x,
            y
          }

          setCrop(c)
          setCompletedCrop(c)
        }
      },
      [show]
    )

    useEffect(() => {
      window.addEventListener('resize', fullImageCrop)
      return function cleanup() {
        window.removeEventListener('resize', fullImageCrop)
      }
    }, [toCrop, fullImageCrop])

    const cropImage = () => {
      if (!rImg.current) return

      const image = rImg.current
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (completedCrop) {
        const crop = completedCrop
        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        canvas.width = crop.width * pixelRatio
        canvas.height = crop.height * pixelRatio

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
        ctx.imageSmoothingEnabled = false

        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        )
      } else {
        canvas.width = image.naturalWidth
        canvas.height = image.naturalHeight

        ctx.drawImage(
          image,
          0,
          0,
          image.naturalWidth,
          (canvas.height = image.naturalHeight),
          0,
          0,
          image.naturalWidth,
          (canvas.height = image.naturalHeight)
        )
      }

      const dataUrl = canvas.toDataURL()

      return dataUrl
    }

    const dataURItoBlob = dataURI => {
      // convert base64/URLEncoded data component to raw binary data held in a string
      var byteString
      if (dataURI.split(',')[0].indexOf('base64') >= 0)
        byteString = atob(dataURI.split(',')[1])
      else byteString = unescape(dataURI.split(',')[1])

      // separate out the mime component
      var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

      // write the bytes of the string to a typed array
      var ia = new Uint8Array(byteString.length)
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }

      return new Blob([ia], { type: mimeString })
    }

    const handleOk = async () => {
      //const cropped = cropImage()
      //const blob = dataURItoBlob(cropped)
      //var file = new File([blob], 'myroom.jpeg', { type: 'image/jpeg' })
      logEvent('my_decor_upload_complete', {
        content_type: 'my_decor'
      })

      setSubmitting(true)
      if (!toCrop) {
        //rCrop.current.hide()
        setSubmitting(false)
        return
      }

      const cropped = cropImage()
      // console.log(cropped)
      //const blob = dataURItoBlob(cropped)
      //console.log(blob)
      const imageData = {
        __type: 'Bytes',
        base64: cropped
      }

      await createMyDecor(imageData, { category })
      dispatch(fetchFeed('mydecor')({ status: FEED_RESET }))
      if (onComplete) onComplete()
      setSubmitting(false)
    }

    const handleRemoveBG = async e => {
      e.preventDefault()
      e.stopPropagation()
      setSubmitting(true)

      const cropped = cropImage()
      const blob = dataURItoBlob(cropped)
      const mimeString = cropped.split(',')[0].split(':')[1].split(';')[0]
      const ext = mimeString.split('/')[1]

      const filename = randomNodeId() + '.' + ext

      const file = new File([blob], filename, { type: mimeString })

      const result = await removebg(file)
      if (result) {
        if (!onRemoveBGComplete) return
        onRemoveBGComplete(result)
      }
      setSubmitting(false)
      setRemoveStatus('complete')
    }

    useImperativeHandle(ref, () => ({
      setImage: imgfile => {
        //setImage(imgfile)
      },
      show: imgfile => {
        //setImage(imgfile)
        //rCrop.current.show()
        setRemoveStatus('none')
        dispatch(setModalOpen())
      },
      hide: () => {
        setRemoveStatus('none')
        setSubmitting(false)
        dispatch(setModalClose())
        //rCrop.current.hide()
      }
    }))

    const onLoad = useCallback(
      img => {
        rImg.current = img

        var w = img.naturalWidth
        var h = img.naturalHeight

        if (w > VIEW_MAX_WIDTH || h > VIEW_MAX_HEIGHT) {
          if (w >= h) {
            const sx = VIEW_MAX_WIDTH / w
            //setViewScale(sx)
            setViewImgWidth(VIEW_MAX_WIDTH + 'px')
            const sh = sx * h
            //setViewImgHeight(sh + 'px')
            w = VIEW_MAX_WIDTH
            h = sh
          } else {
            const sy = VIEW_MAX_HEIGHT / h
            //setViewScale(sy)
            //setViewImgHeight(VIEW_MAX_HEIGHT + 'px')
            const sw = sy * w
            setViewImgWidth(sw + 'px')
            w = sw
            h = VIEW_MAX_HEIGHT
          }
        } else {
          //setViewScale(1.0)
          setViewImgWidth(w + 'px')
          //setViewImgHeight(h + 'px')
        }

        fullImageCrop({ w, h })
        return false
      },
      [fullImageCrop]
    )

    return (
      <CropMyDecorModalContainer
        show={show}
        title=""
        scheme={appearance}
        maxWidth={800}
        noOverlayClose
        onClose={onClose}
        size={DMModal.MODAL_SIZE_FULLSCREEN}
        {...props}
      >
        <ModalContentContainer draggable="false">
          <CropContainer>
            <ReactCrop
              src={toCrop}
              onImageLoaded={onLoad}
              crop={crop}
              minWidth={150}
              minHeight={150}
              keepSelection={true}
              ruleOfThirds={true}
              onChange={c => setCrop(c)}
              onComplete={c => setCompletedCrop(c)}
              imageStyle={cropAreaStyles}
            />
            {submitting && (
              <CropOverlay>
                <Loading>
                  <Player
                    loop
                    autoplay={true}
                    src={circle}
                    style={{
                      height: '300px',
                      width: '300px'
                    }}
                  />
                </Loading>
              </CropOverlay>
            )}
          </CropContainer>

          <ModalActionContainer>
            {removeStatus === 'none' && (
              <>
                <DMPrimaryAltButton
                  scheme={appearance}
                  type="button"
                  onClick={handleRemoveBG}
                  style={{ marginRight: '30px' }}
                  disabled={submitting}
                >
                  Remove Background
                </DMPrimaryAltButton>
                <DMPrimaryButton
                  scheme={appearance}
                  type="button"
                  onClick={handleOk}
                  style={{ marginLeft: '30px' }}
                  disabled={submitting}
                >
                  Save to MyDecor
                </DMPrimaryButton>
              </>
            )}
            {removeStatus === 'complete' && (
              <>
                <DMPrimaryButton
                  scheme={appearance}
                  type="button"
                  onClick={handleOk}
                  style={{ marginLeft: '30px' }}
                  disabled={submitting}
                >
                  Use it Now
                </DMPrimaryButton>
              </>
            )}
          </ModalActionContainer>
        </ModalContentContainer>
      </CropMyDecorModalContainer>
    )
  }
)

export default CropMyDecorModal
